import { call, take, put, delay } from 'redux-saga/effects';
import axios from 'axios';
import { devPayload, errPayload } from 'sagas/hardCodedPayloads/getUserInfo';
import parseUserInfo from './functions/parse_user_info';
import { RECORD_USER_LOGIN } from 'actions/eventActions';
import { SET_USER_INFO, CREATE_ERROR, RECORD_ERROR_SAGA } from '../actions';

// const url_string = window.location.href;
// const url = new URL(url_string);
// const stage = url.href.includes('https://www.yisumatica.org.il/stage')

const userInfoEndpoint        = 'https://www.yisumatica.org.il/chk_session';
const userPermissionsEndpoint = 'https://www.yisumatica.org.il/yisumaticaApiZvi/getUserPermissions.php';
// const userPermissionsEndpoint = stage
//   ? 'https://www.yisumatica.org.il/api/getUserPermissionsTest.php'
//   : 'https://www.yisumatica.org.il/api/getUserPermissions.php';

const SESSION_MAX_ATTEMPTS = 5;
const SESSION_RETRY_DELAY_MS = 2000;
const SESSION_POLL_LIMIT = 30; // wait up to ~1 minute before giving up on chk_session
const FULL_GRADES = ['1','2','3','4','5','6','7'];

const generateClientRequestId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function hasSessionIdentity(sessionStr: string): boolean {
  if (!sessionStr) {
    return false;
  }
  return /(?:name=|mail=|user=|semel_mosad=|semelMosad=|semelMossad=)/.test(sessionStr);
}

function* getUserInfo(): Generator<any, any, any> {
  while (true) {
    try {
      yield take('GET_USER_INFO_SAGA');

      if (process.env.NODE_ENV === 'development') {
        yield put(SET_USER_INFO(devPayload));
        yield put(RECORD_USER_LOGIN(devPayload));
        continue;
      }

      // 1) Poll chk_session until identity exists BEFORE hitting PHP (UI already has provisional data)
      let session = '';
      let sessionReady = false;
      let sessionPollAttempt = 0;
      while (!sessionReady) {
        const userInfo = yield call(axios.get, userInfoEndpoint, { withCredentials: true });
        const userInfoData: string = userInfo.data;
        const bodyStart = userInfoData.search('name=');
        const bodyEnd = userInfoData.search('</body>');
        session = bodyStart >= 0 && bodyEnd >= 0 ? userInfoData.slice(bodyStart, bodyEnd) : userInfoData;
        sessionReady = hasSessionIdentity(session);

        if (sessionReady) {
          break;
        }

        sessionPollAttempt += 1;
        if (sessionPollAttempt >= SESSION_POLL_LIMIT) {
          throw new Error('SESSION_IDENTITY_TIMEOUT');
        }

        yield delay(SESSION_RETRY_DELAY_MS);
      }
      
      // Debug: log session extraction
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[getUserInfo] Session string extracted:', session);
      }

      let {
        name,
        email,
        role,
        logedIn,
        semelMosad,
        plus,
        gradeAccess,
        subscriptionExpired,
        subscriptionExpiresAt,
        userFoundInTables,
      } = parseUserInfo(session);
      let userExistsValue = -1;
      let permissionResolved = false;
      sessionReady = true;
      const clientRequestId = generateClientRequestId();
      let clientSessionRaw = session || '';
      
      // Debug: log parsed name
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[getUserInfo] Parsed name:', name);
      }

      // 2) שלח Payload ראשוני מיידי כדי שהאתר ייטען בלי להמתין לתשובת PHP
      const provisionalPayload = {
        userLogedIn:           logedIn,
        semelMosad:            semelMosad,
        userName:              name,
        userEmail:             email,
        userId:                email && !email.includes('@') ? email : '',
        userRole:              role,
        gradeAccess:           FULL_GRADES,
        plus:                  true,
        subscriptionExpired:   subscriptionExpired,
        subscriptionExpiresAt: subscriptionExpiresAt,
        userFoundInTables:     userFoundInTables,
        userExists:            -1,
        session:               session,
        permissionResolved:    false,
        blockUser:             false,
        allowAppAccess:        undefined,
      };

      localStorage.setItem('yisumaticaPermissions', JSON.stringify(provisionalPayload));
      yield put(SET_USER_INFO(provisionalPayload));

      // 3) תמיד קרא ל־getUserPermissions.php — ה־PHP קורא את ה־session מה־cookies,
      //    מזהה אורח/משתמש, ומחזיר grades/plus/expired/blockUser/allowAppAccess.
      let res;
      for (let attempt = 0; attempt < SESSION_MAX_ATTEMPTS; attempt++) {
        const requestBody = {
          clientId: 'react_frontend',
          clientRequestId,
          clientOverrideToken: 'QWERASD1232',
          clientSessionRaw,
        };

        res = yield call(
          axios.post,
          userPermissionsEndpoint,
          requestBody,
          { withCredentials: true }
        );

        if (res && res.data && (res.data.grades || res.data.success)) {
          permissionResolved = true;
          gradeAccess             = FULL_GRADES;
          plus                    = true as any;
          semelMosad              = res.data.semelMosad ?? semelMosad;
          subscriptionExpired     = res.data.userSubscriptionExpired ?? subscriptionExpired;
          subscriptionExpiresAt   = res.data.userSubscriptionExpiresAt ?? subscriptionExpiresAt;
          userFoundInTables       = res.data.userFoundInTables ?? userFoundInTables;
          if (typeof res.data.userExists === 'boolean') {
            userExistsValue = res.data.userExists ? 1 : 0;
          } else {
            userExistsValue = -1;
          }
          break;
        }

        if (attempt < SESSION_MAX_ATTEMPTS - 1) {
          yield delay(SESSION_RETRY_DELAY_MS);
          const refreshedInfo = yield call(axios.get, userInfoEndpoint, { withCredentials: true });
          const refreshedBody = refreshedInfo.data;
          const bodyStart = refreshedBody.search('name=');
          const bodyEnd = refreshedBody.search('</body>');
          session = bodyStart >= 0 && bodyEnd >= 0 ? refreshedBody.slice(bodyStart, bodyEnd) : refreshedBody;
          ({
            name,
            email,
            role,
            logedIn,
            semelMosad,
            plus,
            gradeAccess,
            subscriptionExpired,
            subscriptionExpiresAt,
            userFoundInTables,
          } = parseUserInfo(session));
          clientSessionRaw = session || '';
          sessionReady = hasSessionIdentity(session);
          if (sessionReady) {
            continue;
          }
        }
      }

      if (!permissionResolved) {
        yield put(CREATE_ERROR());
      }

      // 4) בונים payload סופי ומעדכנים מחדש את הסטייט
      const blockUser      = !!(res && res.data && res.data.blockUser);
      const allowAppAccess = res?.data?.allowAppAccess;

      const payload = {
        userLogedIn:           logedIn,
        semelMosad:            semelMosad,
        userName:              name,
        userEmail:             email,
        userId:                email && !email.includes('@') ? email : '',
        userRole:              role,
        gradeAccess:           gradeAccess ?? FULL_GRADES,
        plus:                  plus ?? true,
        subscriptionExpired:   subscriptionExpired,
        subscriptionExpiresAt: subscriptionExpiresAt,
        userFoundInTables:     userFoundInTables,
        userExists:            userExistsValue,
        session:               session,
        permissionResolved,
        // אופציונלי – שימושי למסכים שחוסמים לפי שרת:
        blockUser,
        allowAppAccess,
      };

      // אחסון ועדכון סטייט
      if (permissionResolved) {
        localStorage.setItem('yisumaticaPermissions', JSON.stringify(payload));
        yield put(SET_USER_INFO(payload));
        if (blockUser) {
          yield put(RECORD_USER_LOGIN(payload));
        }
      }

    } catch (err) {
      yield put(RECORD_ERROR_SAGA({ data: { ...errPayload, error: err }, endpoint: 'get_user_info' }));
      yield put(SET_USER_INFO(errPayload));
      yield put(CREATE_ERROR());
      // eslint-disable-next-line no-console
      console.log('err  :  ' + err);
    } finally {
      // no-op
    }
  }
}

export default getUserInfo;
